import { FC, Fragment, useEffect, useState } from 'react';
import { PackageDetails } from 'ts/package/interfaces/PackageDetails';
import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { useTranslation } from 'react-i18next';
import { formatPrice, showWarning } from 'utils/utils';
import { getCurrency, formatPriceCurrency } from 'utils/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowCircleDown, faArrowCircleUp } from '@fortawesome/free-solid-svg-icons';
import { formatOccupancySrt, getFare, getInfantPrice, getSeats } from 'utils/package';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface RoomsDetailsProps {
  dealModel: PackageDetails;
  onChangePax: (
    rooms: string[],
    totalPrice: number,
    totalAvgPrice: number,
    infants: string[],
    infantsCount: number
  ) => void;
  rooms: string[];
  infants: string[];
}

interface Room {
  options: Select[];
  code: string;
  roomType: string;
  price: number;
  occupancyStr: string;
  choosen: number;
  currency: string;
  paxes: number;
  visible: boolean;
  bookCode: number;
  fareBasis: string;
  infants: number[];
}

const RoomsDetails: FC<RoomsDetailsProps> = ({
  dealModel: { deal, options, fareBasisBoardDesc, flights },
  onChangePax,
  ...props
}) => {
  const { t, i18n } = useTranslation();
  const [availableRooms, setAvailableRooms] = useState<Room[]>([]);
  const [open, setOpen] = useState(false);

  const seats = getSeats(options, flights);
  const infantPrice = getInfantPrice(options);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalAvgPrice, setTotalAvgPrice] = useState(0);
  const [infantOptions, setInfantOptions] = useState<Select[]>([]);

  useEffect(() => {
    if (options.length) {
      let rooms = options[0].prices
        .map((item) => {
          const options = [...Array(4)].map((_, index) => {
            let label = '';
            if (index === 0) {
              label = t('hotel.choose_rooms');
            } else if (index === 1) {
              label = t('hotel.single_room', {
                price: formatPriceCurrency(item.salePrice, getCurrency(item.currency)),
              });
            } else {
              label = t('hotel.multi_rooms', {
                price: formatPriceCurrency(item.salePrice * index, getCurrency(item.currency)),
                count: index,
              });
            }

            return {
              label,
              value: index,
            };
          });

          return {
            options,
            code: item.complect,
            roomType: item.roomType,
            price: item.salePrice,
            currency: item.currency,
            occupancyStr: formatOccupancySrt(item.complect),
            paxes: item.complect.length,
            choosen: 0,
            visible: item.complect === deal.paxComplect,
            bookCode: item.bookCode,
            fareBasis: item.fareBasis,
            infants: [] as number[],
          };
        })
        .sort((a, b) => (a.visible === b.visible ? 0 : a.visible ? -1 : 1));

      let newtotalPrice = rooms[0].price;
      let newtotalAvgPrice = rooms[0].price / rooms[0].paxes;

      if (rooms[0].paxes > (seats || 0)) {
        showWarning(t('hotel.specific_out'));
      }

      rooms[0].choosen = 1;
      onChangePax(
        [rooms[0].bookCode + '-' + rooms[0].choosen],
        rooms[0].price,
        rooms[0].price / rooms[0].paxes,
        [],
        0
      );

      if (infantPrice) {
        setInfantOptions([
          { value: 0, label: '0' },
          { value: 1, label: `1` },
          { value: 2, label: `2` },
        ]);
      }

      if (props.rooms.length) {
        props.rooms.forEach((room) => {
          const roomIndex = rooms.findIndex((r) => r.bookCode === +room.split('-')[0]);
          if (roomIndex !== -1) {
            rooms[roomIndex].choosen = +room.split('-')[1];
          }
        });

        const totals = getTotals(rooms, 0);
        newtotalPrice = totals.newTotalPrice;
        newtotalAvgPrice = totals.newTotalAvgPrice;
        onChangePax(
          totals.newSelectedRooms,
          totals.newTotalPrice,
          totals.newTotalAvgPrice,
          props.infants,
          0
        );
      }

      if (props.infants.length) {
        const groupedInfants = props.infants.reduce((acc: [string, number[]][], infant) => {
          const [roomCode, count] = infant.split('-');
          const existingGroup = acc.find((group) => group[0] === roomCode);

          if (existingGroup) {
            existingGroup[1].push(+count);
          } else {
            acc.push([roomCode, [+count]]);
          }
          return acc;
        }, []);

        const infantsNew = groupedInfants.map(([roomCode, counts]) => {
          return `${roomCode}-${counts.join(',')}`;
        });

        const infantsCount = props.infants.reduce((acc, infant) => {
          const [, counts] = infant.split('-');
          return acc + +counts;
        }, 0);

        infantsNew.forEach((infant) => {
          const [roomCode, counts] = infant.split('-');
          const roomIndex = rooms.findIndex((r) => r.bookCode === +roomCode);
          if (roomIndex !== -1) {
            rooms[roomIndex].infants = Array(rooms[roomIndex].choosen || 1).fill(0);
            counts.split(',').forEach((count, index) => {
              rooms[roomIndex].infants[index] = +count;
            });
          }
        });
        const totals = getTotals(rooms, infantsCount);
        newtotalPrice = totals.newTotalPrice;
        newtotalAvgPrice = totals.newTotalAvgPrice;
        onChangePax(
          totals.newSelectedRooms,
          totals.newTotalPrice,
          totals.newTotalAvgPrice,
          props.infants,
          infantsCount
        );
      }
      setAvailableRooms(rooms);
      setTotalPrice(newtotalPrice);
      setTotalAvgPrice(newtotalAvgPrice);
    }
  }, [i18n.language]); //  eslint-disable-line react-hooks/exhaustive-deps

  const onChangeInfants = (value: number, roomIndex: number, infIndex: number) => {
    const newRooms = [...availableRooms];
    newRooms[roomIndex].infants[infIndex] = value;
    const infantsCount = getInfantsCount(newRooms);

    const { newSelectedRooms, newTotalPrice, newTotalAvgPrice } = getTotals(newRooms, infantsCount);

    onChangePax(
      newSelectedRooms,
      newTotalPrice,
      newTotalAvgPrice,
      generateInfants(newRooms),
      infantsCount
    );
    setTotalPrice(newTotalPrice);
    setTotalAvgPrice(newTotalAvgPrice);
  };

  const getInfantsCount = (rooms: Room[]): number => {
    return rooms
      .filter((room) => room.choosen)
      .reduce(
        (acc, room) => acc + (room.infants?.reduce((acc, infant) => acc + infant, 0) || 0),
        0
      );
  };

  const generateInfants = (rooms: Room[]): string[] => {
    const result = rooms
      .filter((room) => room.choosen)
      .reduce((acc: string[], room) => {
        return [...acc, ...room.infants.map((infant) => room.bookCode + '-' + infant)];
      }, []);

    return result;
  };

  const onSelectRooms = (room: Room, index: number) => {
    const maxRoomCount = 3;

    const newRooms = [...availableRooms];
    newRooms[index] = { ...room, infants: Array(room.choosen || 1).fill(0) };
    const roomsCount = newRooms.reduce((acc, room) => acc + room.choosen, 0);

    const infantsCount = getInfantsCount(newRooms);

    if (roomsCount > maxRoomCount) {
      showWarning(t('hotel.max_rooms', { count: maxRoomCount }));
    } else {
      setAvailableRooms(newRooms);

      const { newSelectedRooms, newTotalPrice, newTotalAvgPrice } = getTotals(
        newRooms,
        infantsCount
      );

      setTotalPrice(newTotalPrice);
      setTotalAvgPrice(newTotalAvgPrice);

      onChangePax(
        newSelectedRooms,
        newTotalPrice,
        newTotalAvgPrice,
        generateInfants(newRooms),
        infantsCount
      );
    }
  };

  const getTotals = (newRooms: Room[], newInfants: number) => {
    const newSelectedRooms = newRooms
      .filter((room) => room.choosen)
      .map((room) => room.bookCode + '-' + room.choosen);

    const newTotal = newRooms.reduce((acc, room) => {
      return acc + room.price * room.choosen;
    }, 0);
    const newTotalPrice = newTotal + newInfants * infantPrice;

    const paxCount =
      newRooms.reduce((acc, room) => {
        return acc + room.choosen * room.paxes;
      }, 0) + newInfants;
    const newTotalAvgPrice = newTotalPrice / paxCount;

    return { newSelectedRooms, newTotalPrice, newTotalAvgPrice };
  };

  const getRoomsCount = () => {
    return availableRooms.reduce((acc, room) => {
      return acc + room.choosen;
    }, 0);
  };

  const onClickOpen = () => {
    setOpen(!open);

    const newRooms = [...availableRooms];
    if (!open) {
      newRooms.forEach((room) => {
        room.visible = true;
      });
    } else {
      newRooms.forEach((room, index) => {
        room.visible = room.code === deal.paxComplect || index === 0;
      });
    }
    setAvailableRooms(newRooms);
  };

  return (
    <div>
      <div className="warning color-warning">
        <div>{t('hotel.please_note')}</div>
        <div>{t('hotel.you_can_choose', { count: seats })}</div>
      </div>

      <div className="table-default mb-20 mt-20">
        <table className="table-bordered-lines table-mobile" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: 170 }}>{t('hotel.rooms_price')}</th>
              {!!infantPrice && (
                <th style={{ width: 130 }}>
                  <div style={{ maxWidth: 110 }}>
                    <div>{t('search.infants')}</div>
                    <div>
                      ({t('search.under')} 2){' '}
                      <span className="text-light text-xsm">
                        ({formatPrice(infantPrice, deal.currency)})
                      </span>
                    </div>
                  </div>
                </th>
              )}
              <th>{t('hotel.paxes')}</th>
              <th style={{ width: 300 }}>{t('hotel.room_type')}</th>
              <th>{t('price')}</th>
            </tr>
          </thead>
          <tbody>
            {availableRooms
              .filter((item) => item.visible)
              .map((item, roomIndex) => {
                return (
                  <tr key={roomIndex}>
                    <td width={200}>
                      <SelectCustom
                        options={item.options}
                        value={item.choosen}
                        onChange={(value) => onSelectRooms({ ...item, choosen: value }, roomIndex)}
                        appendToBody
                      />
                    </td>
                    {!!infantPrice && (
                      <td width={190}>
                        <div className="flex-row">
                          {Array.from({ length: item.choosen || 1 }).map((_, infIndex) => (
                            <span key={infIndex} className="mr-5">
                              <SelectCustom
                                appendToBody
                                options={infantOptions}
                                value={item.infants?.[infIndex] || 0}
                                onChange={(value) => onChangeInfants(value, roomIndex, infIndex)}
                              />
                            </span>
                          ))}
                        </div>
                      </td>
                    )}
                    <td className="text-sm">{item.occupancyStr}</td>
                    <td className="text-sm">
                      <div className="text-cap mb-3">{item.roomType?.toLowerCase()}</div>
                      <div className="text-xsm text-light">
                        {getFare(item.fareBasis, fareBasisBoardDesc)}
                      </div>
                    </td>
                    <td className="pl-10 text-bold text-contrast">
                      {formatPriceCurrency(item.price, getCurrency(item.currency))}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="flex-between ph-10">
        {availableRooms.length > 1 && (
          <div className="flex-row-end mb-20 text-sm cur-poi" onClick={onClickOpen}>
            <FontAwesomeIcon
              icon={open ? faArrowCircleUp : faArrowCircleDown}
              className="mr-5 text-contrast"
            />
            {t('hotel.more_room_types', {
              count: availableRooms.filter((item) => item.code !== deal.paxComplect).length,
            })}
          </div>
        )}
      </div>

      <div className="flex-between">
        <div>
          {!!totalAvgPrice && !!totalPrice && (
            <Fragment>
              <div className="text-st text-contrast text-bold mb-5">
                {t('flight.total_average_passenger')}:{' '}
                {formatPriceCurrency(totalAvgPrice, getCurrency(deal.currency))}
              </div>
              <div className="text-xsm text-light">
                {t('total')}: {formatPriceCurrency(totalPrice, getCurrency(deal.currency))}
              </div>
            </Fragment>
          )}
        </div>
        <div className="text-md text-bold">
          {t('hotel.rooms')}: {getRoomsCount()}
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(RoomsDetails, 'RoomsDetails');
